import React, { useContext, useState } from "react";
import "./Register.css";
import { Link, useLocation } from "react-router-dom";
import {
  postLogin,
  postRegister,
  setDefaultHeader,
  postVerifyRegistrationWithoutOtp,
} from "../../core/api/Api";
import { useTranslation } from "react-i18next";
import {
  setCookie,
  showToast,
  buildLocalizedPath,
} from "../../core/common-service/CommonService";
import { LoginSocialGoogle } from "reactjs-social-login";
import { StoreContext } from "../../core/context/StoreContext";
import { useLocalizedUrl } from "../../components/common/useLocalizedUrl";
import useLangNavigate from "../common/useLangNavigate";
import Captcha from "../_components/Captcha";
import { LoginSignUpEvent } from "../../core/utils/loginSignupEvent";

function Register() {
  const { setIsLoggedIn, setUser } = useContext(StoreContext);

  const { t } = useTranslation();
  const { getUrl } = useLocalizedUrl();
  const location = useLocation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [confirm_email, setConfirmEmail] = useState("");
  const [city, setCity] = useState("");
  const [password, setPassword] = useState("");
  const [confirm_password, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isTermsAgreed, setIsTermsAgreed] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const navigate = useLangNavigate();

  const onFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };

  const onLastNameChange = (e) => {
    setLastName(e.target.value);
  };

  const onEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const onConfirmEmailChange = (e) => {
    setConfirmEmail(e.target.value);
  };

  const onCityChange = (e) => {
    setCity(e.target.value);
  };

  const onPasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const onConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const onShowHidePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const onTermsAgreeChange = (e) => {
    setIsTermsAgreed((prev) => !prev);
  };

  const handleSubmit = (e) => {
    if (isLoading) return;
    setIsLoading(true);

    if (password !== confirm_password) {
      showToast({ type: "error", message: t("error.password_does_not_match") });
      setIsLoading(false);
      return;
    }

    if (email !== confirm_email) {
      showToast({
        type: "error",
        message: t("error.email_does_not_match"),
      });
      setIsLoading(false);
      return;
    }

    if (!captchaToken) {
      showToast({
        type: "error",
        message: t("error.complete_captcha"),
      });
      setIsLoading(false);
      return;
    }

    if (!isTermsAgreed) {
      showToast({
        type: "error",
        message: t("error.please_accept_terms_conditions"),
      });
      setIsLoading(false);
    } else {
      postRegister({
        first_name: firstName,
        last_name: lastName,
        email,
        city,
        password,
        confirm_password,
      })
        .then((res) => {
          const status_code = res?.status;
          if (status_code === 200) {
            const trial_session = localStorage.getItem("trial_session");
            postVerifyRegistrationWithoutOtp({ email, trial_session })
              .then((res) => {
                const status_code = res?.status;
                const response = res?.data?.success;
                if (status_code === 200) {
                  const token = response?.token;
                  const user = response?.user;
                  if (user?.is_verified) {
                    localStorage.setItem("isGoogleLogin", "false");
                    LoginSignUpEvent({
                      user: user,
                      is_new: true,
                      is_google: false,
                    });
                    const service_session_id =
                      response?.service_session_id ?? null;
                    setDefaultHeader("Bearer " + token);
                    setIsLoggedIn(true);
                    setUser(user);
                    setTimeout(() => {
                      setCookie("token", token);
                      navigate("/thankyou/register");
                      if (service_session_id) {
                        localStorage.removeItem("trial_session");
                      }
                    }, 300);
                  }
                }
              })
              .catch((e) => {});
          } else if (status_code === 202) {
            setIsLoading(false);
            navigate("/login");
          } else {
            setIsLoading(false);
            showToast({
              type: "error",
              message: t("error.error_while_registering"),
            });
          }
        })
        .catch((e) => {
          const response = e?.response;
          const status_code = response?.status;
          if (status_code === 409) {
            setIsLoading(false);
          }
          setIsLoading(false);
        });
    }
  };

  const _postLogin = (body) => {
    postLogin(body)
      .then((res) => {
        setIsLoading(false);
        const status_code = res?.status;
        const response = res?.data?.success;
        if (status_code === 200) {
          const token = response?.token;
          const user = response?.user;
          if (user?.is_verified) {
            localStorage.setItem("isGoogleLogin", "true");
            const service_session_id = response?.service_session_id ?? null;
            setDefaultHeader("Bearer " + token);
            setIsLoggedIn(true);
            setUser(user);
            setTimeout(() => {
              setCookie("token", token);
              if (service_session_id) {
                navigate("/questions/" + service_session_id);
                localStorage.removeItem("trial_session");
              } else {
                const from = location.state?.from || "/";
                navigate(from, "", { state: location.state });
              }
            }, 300);
          } else {
            showToast({ type: "error", message: t("error.user_not_allowed") });
          }
        }
      })
      .catch((e) => {
        const response = e?.response;
        const status_code = response?.status;
        if (status_code === 400) {
          navigate("/thankyou/register", "", { state: { email } });
        }
        setIsLoading(false);
      });
  };

  const onResolveGoogleLogin = ({ provider, data }) => {
    setIsLoading(true);
    const trial_session = localStorage.getItem("trial_session");
    _postLogin({
      access_token: data.access_token,
      trial_session,
      given_name: data.given_name,
      family_name: data.family_name,
    });
  };

  return (
    <section className="aai-signup-in">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-5 col-md-7 col-sm-9">
            <div className="aai-form-wrapper-new">
              {/* Header */}
              <div className="aai-form-header-new text-center mb-4">
                <h2 className="aai-form-title-new">
                  {t("signup_for_an_account")}
                </h2>
              </div>

              {/* Google Sign Up Button */}
              <LoginSocialGoogle
                scope={"https://www.googleapis.com/auth/userinfo.email"}
                client_id={process.env.REACT_APP_GG_APP_ID || ""}
                onResolve={onResolveGoogleLogin}
                onReject={(err) => {
                  showToast({
                    type: "error",
                    message: t("error.unknown_error"),
                  });
                }}
              >
                <button className="aai-google-btn-new w-100 mb-4">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    className="me-2"
                  >
                    <path
                      d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                      fill="#4285F4"
                    />
                    <path
                      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
                      fill="#34A853"
                    />
                    <path
                      d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.582C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                      fill="#EA4335"
                    />
                  </svg>
                  {t("signup")} {t("with")} Google
                </button>
              </LoginSocialGoogle>

              {/* Form Fields */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                }}
              >
                <div className="mb-3">
                  <label className="aai-form-label-new">
                    {t("email_address")}
                  </label>
                  <input
                    className="aai-form-input-new"
                    type="email"
                    placeholder={t("enter_your_email")}
                    value={email}
                    onChange={onEmailChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="aai-form-label-new">
                    {t("confirm_email_address")}
                  </label>
                  <input
                    className="aai-form-input-new"
                    type="email"
                    placeholder={t("enter_your_email_again")}
                    value={confirm_email}
                    onChange={onConfirmEmailChange}
                    onPaste={(e) => e.preventDefault()}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="aai-form-label-new">{t("password")}</label>
                  <div className="position-relative">
                    <input
                      className="aai-form-input-new"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("enter_your_password")}
                      value={password}
                      onChange={onPasswordChange}
                      required
                    />
                    <button
                      type="button"
                      className="aai-password-toggle-new"
                      onClick={onShowHidePassword}
                    >
                      <i
                        className={
                          "fa-regular fa-eye" + (showPassword ? "" : "-slash")
                        }
                      ></i>
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="aai-form-label-new">
                    {t("confirm_password")}
                  </label>
                  <div className="position-relative">
                    <input
                      className="aai-form-input-new"
                      type={showPassword ? "text" : "password"}
                      placeholder={t("enter_your_password_again")}
                      value={confirm_password}
                      onChange={onConfirmPasswordChange}
                      required
                    />
                    <button
                      type="button"
                      className="aai-password-toggle-new"
                      onClick={onShowHidePassword}
                    >
                      <i
                        className={
                          "fa-regular fa-eye" + (showPassword ? "" : "-slash")
                        }
                      ></i>
                    </button>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="aai-form-label-new">
                      {t("first_name")}
                    </label>
                    <input
                      className="aai-form-input-new"
                      type="text"
                      placeholder={t("enter_your_first_name")}
                      value={firstName}
                      onChange={onFirstNameChange}
                      required
                    />
                  </div>
                  <div className="col-6">
                    <label className="aai-form-label-new">
                      {t("last_name")}
                    </label>
                    <input
                      className="aai-form-input-new"
                      type="text"
                      placeholder={t("enter_your_last_name")}
                      value={lastName}
                      onChange={onLastNameChange}
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="aai-form-label-new">{t("city")}</label>
                  <input
                    className="aai-form-input-new"
                    type="text"
                    placeholder={t("enter_your_city")}
                    value={city}
                    onChange={onCityChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <div className="d-flex align-items-start">
                    <input
                      type="checkbox"
                      className="aai-checkbox-new me-2"
                      checked={isTermsAgreed}
                      onChange={onTermsAgreeChange}
                      id="terms-checkbox"
                    />
                    <label
                      htmlFor="terms-checkbox"
                      className="aai-terms-label-new"
                    >
                      {t("accept_privacy_terms_checkbox")}{" "}
                      <Link
                        to={getUrl("/conditions-generales-dutilisation/")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aai-terms-link-new"
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(
                            getUrl("/conditions-generales-dutilisation/"),
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                      >
                        {t("terms_and_conditions")}
                      </Link>
                    </label>
                  </div>
                </div>

                <div className="mb-4">
                  <Captcha onVerify={(token) => setCaptchaToken(token)} />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="aai-submit-btn-new w-100 mb-4"
                >
                  {isLoading ? "Loading..." : t("signup")}
                </button>
              </form>

              {/* Footer */}
              <div className="text-center">
                <p className="aai-form-footer-new">
                  {t("already_have_an_account")}?{" "}
                  <Link
                    to={buildLocalizedPath("/login")}
                    className="aai-signin-link-new"
                  >
                    {t("signin")}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Register;
